import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Banner } from "@/components/ui/banner";
import {
  CALIBRATION_CASE_VIDEO_QUERY_KEY,
  useCalibrationCaseVideo,
} from "@/hooks/queries/useCalibrationCaseVideo";

// The shared calibration case-video player (seam-82). Extracted from
// CalibrationCaseLogScreen at Story 8.10 so the read-only case log (Screen 18)
// and the scoring workspace (Screen 17) mount the SAME player — one place owns
// the pre-signed-URL lifecycle, the caption exemption, and the failure posture.
//
// Plays the pre-signed URL inline; ANY failure — CAL-503 (DevOps #430
// unprovisioned), offline, an expiry race — renders the calm info Banner instead
// of the player. Never a retry storm (the hook is retry: false), never a broken
// shell. Quiet while pending.

/**
 * @param caseId  The calibration case whose video to play.
 * @param unaffectedNote  Trailing reassurance in the failure Banner. The two
 *   hosts mean different things by "your scores are fine": the log shows already
 *   recorded scores, the scoring view holds unsaved entry in local state.
 */
export function CaseVideo({
  caseId,
  unaffectedNote = "Your recorded scores below are unaffected.",
}: {
  caseId: string;
  unaffectedNote?: string;
}) {
  const { data, isPending, isError } = useCalibrationCaseVideo(caseId);
  const queryClient = useQueryClient();
  // The URL that failed to play, so a successful re-mint (a DIFFERENT url)
  // clears the banner on its own without an effect.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const remintedRef = useRef(false);

  const url = data?.video_url;

  /**
   * The media element errored — the query still reports `success`, so nothing
   * recovers unless we act (the pre-signed signature typically outlived
   * `expires_in` mid-session: a long pause, a paused tab, clock skew).
   *
   * Re-mint EXACTLY ONCE per mount. Unbounded re-minting would turn a genuinely
   * dead asset (missing S3 object) into a fetch storm against an endpoint that
   * writes a `calibration.case.fetched` audit row per call — the retry: false
   * posture this file inherits exists for that reason.
   */
  function handlePlaybackError() {
    setFailedUrl(url ?? null);
    if (remintedRef.current) return;
    remintedRef.current = true;
    void queryClient.invalidateQueries({
      queryKey: [CALIBRATION_CASE_VIDEO_QUERY_KEY, caseId],
    });
  }

  if (isPending) return null;
  if (isError || !url || failedUrl === url) {
    return (
      <Banner variant="info" title="Case video unavailable">
        <p>The case video can’t be played right now. {unaffectedNote}</p>
      </Banner>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)]">
      {/* The CITS-delivered calibration videos ship without caption tracks
          (the DevOps #430 asset set has no .vtt files) — there is no track to
          mount, and a fabricated empty <track> would falsely signal captions to
          AT. Revisit if the client supplies captioned assets. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        controls
        src={url}
        onError={handlePlaybackError}
        aria-label="Calibration case video"
        className="aspect-video w-full"
      />
    </div>
  );
}

export default CaseVideo;
